import React from 'react';
import { ApiClient } from '@api/api-configuration';
import {
  BillingEntityTypeEnum,
  BillingTransactionTypeEnum,
  CustomerProfileEntry,
} from '@api/READ_ONLY/billing_api/Api';
import { getRecordTypeLabelKey } from '@app/features/billing/CustomerProfile/recordTypeLabels';
import { APP_PREFIX, CUSTOMER_PROFILE_PAGE } from '@app/routes/urls';
import { currencyFormatter } from '@app/utils/pricingCurrencyUtils';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Drawer, Table, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { formatDate } from '../clientCard.format';
import { cardDrawerWidth } from './VisitDrawer';

/** Enough movements to answer "what is this balance made of" without paging. */
const LEDGER_PAGE_SIZE = 50;

type BillingDrawerProps = {
  open: boolean;
  clientId: number;
  clientName: string;
  /** `null` when the balance is unknown. Rendered as an em-dash, never as zero. */
  balance: number | null;
  lastMovement: string | null;
  onClose: () => void;
};

/**
 * What the client's balance is made of.
 *
 * There are no invoices in this system — the balance is the running total of
 * billing movements — so this shows those movements rather than inventing documents
 * that do not exist. Editing them stays on the Customer Profile page, which the
 * footer links to: money should be changed in one place, not two.
 */
const BillingDrawer: React.FC<BillingDrawerProps> = ({
  open,
  clientId,
  clientName,
  balance,
  lastMovement,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const ledgerQuery = useQuery({
    queryKey: ['client-card-ledger', clientId],
    // Only once the reader asks: most visits to this page never open the drawer.
    enabled: open && clientId > 0,
    queryFn: async () => {
      const response = await ApiClient.billingApi.billing.getCustomerProfile({
        entityType: BillingEntityTypeEnum.Client,
        entityId: clientId,
        offset: 0,
        limit: LEDGER_PAGE_SIZE,
      });
      return response.data;
    },
  });

  const entries: CustomerProfileEntry[] = ledgerQuery.data?.data ?? [];
  const total = ledgerQuery.data?.total ?? entries.length;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={cardDrawerWidth(680)}
      title={t('client-card-billing-title', { name: clientName })}
      footer={
        <Button onClick={() => navigate(`/${APP_PREFIX}/${CUSTOMER_PROFILE_PAGE}?id=${clientId}&type=client`)}>
          {t('client-card-open-customer-profile')}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
            {t('client-card-balance')}
          </div>
          <div
            className="mt-0.5 text-[22px] font-semibold tabular-nums"
            style={{ color: balance !== null && balance > 0 ? '#b91c1c' : '#0f172a' }}
          >
            {balance === null ? '—' : currencyFormatter(balance, undefined, i18n.language)}
          </div>
          <div className="mt-0.5 text-[12px] text-slate-500">
            {balance === null
              ? t('client-card-balance-unavailable')
              : lastMovement
                ? t('client-card-last-movement', { date: formatDate(lastMovement, i18n.language) })
                : t('client-card-no-movements')}
          </div>
        </div>

        {ledgerQuery.error && (
          // Distinct from an empty ledger: "No movements." on a fetch that failed
          // tells a coordinator this client has never been charged.
          <Alert type="error" showIcon message={t('client-card-ledger-error')} />
        )}

        <Table
          size="small"
          rowKey={(row) => `${row.kind}-${row.recordId}`}
          loading={ledgerQuery.isLoading}
          dataSource={entries}
          pagination={false}
          locale={{ emptyText: t('client-card-no-movements') }}
          columns={[
            {
              title: t('client-card-ledger-date'),
              dataIndex: 'date',
              render: (date: string) => formatDate(date, i18n.language),
            },
            {
              // The movement's own type, named with the keys the Customer Profile
              // page uses, so the same row does not read two different ways in two
              // places.
              title: t('client-card-ledger-kind'),
              dataIndex: 'recordType',
              render: (recordType: string) => (
                <Tag className="!mr-0">{t(getRecordTypeLabelKey(recordType as BillingTransactionTypeEnum))}</Tag>
              ),
            },
            { title: t('client-card-ledger-notes'), dataIndex: 'notes', ellipsis: true },
            {
              title: t('client-card-ledger-amount'),
              dataIndex: 'amount',
              align: 'right' as const,
              render: (amount: number) => currencyFormatter(amount, undefined, i18n.language),
            },
            {
              title: t('client-card-ledger-running-total'),
              dataIndex: 'runningTotal',
              align: 'right' as const,
              render: (runningTotal: number | null) =>
                typeof runningTotal === 'number' ? currencyFormatter(runningTotal, undefined, i18n.language) : '—',
            },
          ]}
        />

        {total > entries.length && (
          <div className="text-[12px] text-slate-500">
            {t('client-card-ledger-truncated', { shown: entries.length, total })}
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default BillingDrawer;
